import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';
import PageHeader from '../../components/ui/PageHeader';
import CreatePolicyModal from '../../components/admin/CreatePolicyModal';

const { FiFileText, FiPlus, FiLoader, FiTrash2, FiClock } = FiIcons;

const Policies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const res = await dataFetch('read/policies?sort=created_at&order=desc');
      if (res?.status === 'success') {
        setPolicies(res.data || []);
      }
    } catch (error) {
      console.error("Failed to load policies", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    setDeletingId(id);
    try {
      await dataFetch(`delete/policies/${id}`, { method: 'DELETE' });
      setPolicies(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error("Failed to delete policy", error);
      alert('Failed to delete policy');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSuccess = () => {
    setIsModalOpen(false);
    fetchPolicies();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <PageHeader 
          eyebrow="Admin" 
          title="Policies" 
          description="Manage corporate policies and link them to training modules." 
        />
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm whitespace-nowrap"
        >
          <SafeIcon icon={FiPlus} />
          Create Policy
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">Policy Name</th>
                <th className="px-6 py-4">Version</th>
                <th className="px-6 py-4">Details</th>
                <th className="px-6 py-4">Linked Modules</th>
                <th className="px-6 py-4">Created</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">
                    <SafeIcon icon={FiLoader} className="animate-spin text-2xl mx-auto mb-2" /> 
                    Loading policies...
                  </td>
                </tr>
              ) : policies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">
                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <SafeIcon icon={FiFileText} className="text-2xl text-slate-400" />
                    </div>
                    <p className="font-medium text-slate-700">No policies found</p>
                    <p className="text-xs mt-1">Click "Create Policy" to add your first policy.</p>
                  </td>
                </tr>
              ) : (
                policies.map(policy => {
                  const moduleCount = policy.module_ids ? policy.module_ids.split(',').filter(Boolean).length : 0;
                  
                  return (
                    <tr key={policy.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                            <SafeIcon icon={FiFileText} />
                          </div>
                          <span className="font-semibold text-slate-900">{policy.policy_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-xs font-medium text-slate-600">
                          v{policy.version || '1.0'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-slate-600 line-clamp-2 max-w-xs" title={policy.details}>
                          {policy.details || '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-600 font-medium">
                          {moduleCount} {moduleCount === 1 ? 'Module' : 'Modules'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                          <SafeIcon icon={FiClock} />
                          {policy.created_at ? new Date(policy.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(policy.id)}
                          disabled={deletingId === policy.id}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete Policy"
                        >
                          {deletingId === policy.id ? (
                            <SafeIcon icon={FiLoader} className="animate-spin" />
                          ) : (
                            <SafeIcon icon={FiTrash2} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <CreatePolicyModal 
            onClose={() => setIsModalOpen(false)} 
            onSuccess={handleSuccess} 
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Policies;